import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { trackFunnel } from "@/lib/funnel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { Flame, Briefcase, Loader2, ArrowLeft, ShieldCheck, Check, UserPlus, Search, Wallet, ArrowRight } from "lucide-react";

const NICHES = ["Moda", "Beleza", "Fitness", "Tecnologia", "Gastronomia", "Lifestyle", "Games", "Educação", "Viagem", "Negócios", "Saúde", "Música", "Decoração", "Maternidade", "Pets"];
const INDUSTRIES = ["Moda & Beleza", "Cosméticos", "Saúde & Bem-estar", "Alimentos & Bebidas", "Tecnologia", "Educação", "Finanças", "E-commerce", "Decoração", "Pets", "Automotivo", "Outro"];
const STATES = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

function maskCPF(v) {
  return (v || "").replace(/\D/g, "").slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function maskCNPJ(v) {
  return (v || "").replace(/\D/g, "").slice(0, 14)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}
function maskPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d)/, "($1) $2-$3");
  return d.replace(/(\d{2})(\d{5})(\d)/, "($1) $2-$3");
}

export default function Register() {
  const [params] = useSearchParams();
  const queryRole = params.get("role");
  const initialRole = (() => {
    if (queryRole === "creator" || queryRole === "brand") return queryRole;
    try {
      const saved = localStorage.getItem("alta_preferred_role");
      if (saved === "creator" || saved === "brand") return saved;
    } catch (_) { /* ignore */ }
    return "brand";
  })();
  const [role, setRole] = useState(initialRole);
  const isBrand = role === "brand";
  const { register } = useAuth();
  const nav = useNavigate();

  const switchRole = (next) => {
    if (next === role) return;
    setRole(next);
    try { localStorage.setItem("alta_preferred_role", next); } catch (_) { /* ignore */ }
    const url = `/cadastro?role=${next}`;
    window.history.replaceState({}, "", url);
    trackFunnel("role_selected", { role: next });
  };
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    phone: "", city: "", state: "",
    discovery_source: "",
    cpf: "", birthdate: "", niche: "",
    cnpj: "", company: "", company_legal_name: "", company_size: "", industry: "", website: "", role_title: "",
    accepted_terms: false, accepted_privacy: false, marketing_opt_in: false, legal_responsible_accept: false,
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Funnel: page_view (once per session) + initial role_selected
  useEffect(() => {
    trackFunnel("page_view", { role, once: true });
    trackFunnel("role_selected", { role, once: true });
  }, []);

  // Funnel: form_filled_basic — fire once when name+email+phone+password(≥8) are filled
  const filledBasicRef = useRef(false);
  useEffect(() => {
    if (filledBasicRef.current) return;
    const ok = form.name && form.email && form.phone && form.password && form.password.length >= 8;
    if (ok) {
      filledBasicRef.current = true;
      trackFunnel("form_filled_basic", { role, discovery_source: form.discovery_source || null });
    }
  }, [form.name, form.email, form.phone, form.password, form.discovery_source, role]);

  const canSubmit = (() => {
    if (!form.name || !form.email || !form.password || !form.phone) return false;
    if (form.password.length < 8) return false;
    if (!form.accepted_terms || !form.accepted_privacy) return false;
    if (!isBrand && (!form.cpf || !form.birthdate)) return false;
    if (isBrand && (!form.cnpj || !form.company || !form.legal_responsible_accept)) return false;
    return true;
  })();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    trackFunnel("submit_clicked", { role, discovery_source: form.discovery_source || null });
    try {
      const payload = { ...form, user_type: role };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await register(payload);
      trackFunnel("registration_success", { role, discovery_source: form.discovery_source || null });
      nav("/email-enviado", { replace: true, state: { email: form.email } });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0A0A] flex items-start sm:items-center justify-center px-4 py-10 relative" data-testid="register-page">
      <div className="absolute inset-0 spotlight-light pointer-events-none" />
      <div className="w-full max-w-2xl relative">
        <Link to="/" className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#0A0A0A] text-sm mb-5 w-fit">
          <ArrowLeft className="w-4 h-4" /> Voltar para o início
        </Link>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl warm-gradient flex items-center justify-center glow-red">
            {isBrand ? <Briefcase className="w-5 h-5 text-white" /> : <Flame className="w-5 h-5 text-white" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#DC2626] font-semibold">Cadastro Alta Creators</div>
            <div className="font-display font-bold text-2xl tracking-tight">Crie sua conta em 2 minutos</div>
          </div>
        </div>

        {/* Role toggle */}
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#9B9B9B] font-bold mb-2">Você é…</div>
          <div className="grid grid-cols-2 gap-2 p-1 bg-white border border-[#E8E6E1] rounded-xl" data-testid="role-toggle">
            <button
              type="button"
              onClick={() => switchRole("brand")}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${isBrand ? "bg-[#0A0A0A] text-white shadow-soft-red-lg" : "bg-transparent text-[#4A4A4A] hover:bg-[#FAFAF7]"}`}
              data-testid="role-toggle-brand"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isBrand ? "bg-[#DC2626]" : "bg-[#FFF1F1]"}`}>
                <Briefcase className={`w-4 h-4 ${isBrand ? "text-white" : "text-[#DC2626]"}`} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm leading-tight">Marca / Empresa</div>
                <div className={`text-[11px] mt-0.5 ${isBrand ? "text-white/70" : "text-[#9B9B9B]"}`}>Quero contratar creators</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => switchRole("creator")}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${!isBrand ? "bg-[#0A0A0A] text-white shadow-soft-red-lg" : "bg-transparent text-[#4A4A4A] hover:bg-[#FAFAF7]"}`}
              data-testid="role-toggle-creator"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${!isBrand ? "bg-[#DC2626]" : "bg-[#FFF1F1]"}`}>
                <Flame className={`w-4 h-4 ${!isBrand ? "text-white" : "text-[#DC2626]"}`} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm leading-tight">Creator / UGC</div>
                <div className={`text-[11px] mt-0.5 ${!isBrand ? "text-white/70" : "text-[#9B9B9B]"}`}>Quero criar conteúdo pago</div>
              </div>
            </button>
          </div>
        </div>

        {/* Como funciona em 60 segundos */}
        <div className="mb-6 grid grid-cols-3 gap-2 sm:gap-3" data-testid="onboarding-steps">
          {(isBrand
            ? [
                { i: UserPlus, t: "Cadastre-se", d: "Em 2 minutos" },
                { i: Search, t: "Encontre creators", d: "Match inteligente" },
                { i: Wallet, t: "Aprove e pague", d: "Só pelo que entrega" },
              ]
            : [
                { i: UserPlus, t: "Cadastre-se", d: "Em 2 minutos" },
                { i: Search, t: "Receba briefings", d: "Marcas escolhem você" },
                { i: Wallet, t: "Receba na hora", d: "PIX direto" },
              ]
          ).map((s, idx) => (
            <div key={idx} className="flex flex-col items-center text-center bg-white border border-[#E8E6E1] rounded-lg p-3 sm:p-4 relative">
              <div className="w-9 h-9 rounded-full bg-[#FFF1F1] border border-[#FEE2E2] flex items-center justify-center mb-2">
                <s.i className="w-4 h-4 text-[#DC2626]" />
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#9B9B9B] font-mono mb-0.5">Passo {idx + 1}</div>
              <div className="font-display font-semibold text-sm leading-tight">{s.t}</div>
              <div className="text-[11px] text-[#6B6B6B] mt-0.5 hidden sm:block">{s.d}</div>
              {idx < 2 && (
                <ArrowRight className="hidden sm:block absolute -right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#E8E6E1] bg-[#FAFAF7] rounded-full" />
              )}
            </div>
          ))}
        </div>

        <Card className="bg-white border-[#E8E6E1] p-7 sm:p-9 shadow-soft-red-lg">
          <form onSubmit={submit} className="space-y-7">
            {/* Section 1 — Identity */}
            <section className="space-y-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] font-bold">
                {isBrand ? "1. Dados do responsável" : "1. Seus dados"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-medium">Nome completo *</Label>
                  <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-name" />
                </div>
                <div>
                  <Label className="font-medium">{isBrand ? "Email corporativo" : "Email"} *</Label>
                  <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-medium">WhatsApp / Telefone *</Label>
                  <Input required value={form.phone} onChange={(e) => set("phone", maskPhone(e.target.value))} placeholder="(11) 99999-9999" className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-phone" />
                </div>
                <div>
                  <Label className="font-medium">Senha *</Label>
                  <PasswordInput required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-password" />
                  {form.password ? (
                    <PasswordStrengthMeter password={form.password} testIdPrefix="reg-pw-strength" />
                  ) : (
                    <div className="text-[10px] text-[#9B9B9B] mt-1">Mínimo 8 caracteres · misture letras, números e símbolos</div>
                  )}
                </div>
              </div>
            </section>

            {/* Section 2 — Document / Company */}
            <section className="space-y-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] font-bold">
                {isBrand ? "2. Empresa" : "2. Documento e perfil"}
              </div>
              {isBrand ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-medium">CNPJ *</Label>
                      <Input required value={form.cnpj} onChange={(e) => set("cnpj", maskCNPJ(e.target.value))} placeholder="00.000.000/0000-00" className="mt-1.5 bg-white border-[#E8E6E1] font-mono" data-testid="reg-cnpj" />
                    </div>
                    <div>
                      <Label className="font-medium">Nome fantasia *</Label>
                      <Input required value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Ex: Acme Brasil" className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-company" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-medium">Razão social</Label>
                      <Input value={form.company_legal_name} onChange={(e) => set("company_legal_name", e.target.value)} placeholder="Ex: Acme Comércio LTDA" className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-company-legal" />
                    </div>
                    <div>
                      <Label className="font-medium">Seu cargo</Label>
                      <Input value={form.role_title} onChange={(e) => set("role_title", e.target.value)} placeholder="Ex: CMO, Head de Marketing" className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-role" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-medium">Setor / Indústria</Label>
                      <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                        <SelectTrigger className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-industry"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent className="bg-white">{INDUSTRIES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-medium">Tamanho da empresa</Label>
                      <Select value={form.company_size} onValueChange={(v) => set("company_size", v)}>
                        <SelectTrigger className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-company-size"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="1-10">1 a 10 colaboradores</SelectItem>
                          <SelectItem value="11-50">11 a 50</SelectItem>
                          <SelectItem value="51-200">51 a 200</SelectItem>
                          <SelectItem value="200+">200+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="font-medium">Site (opcional)</Label>
                    <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-website" />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-medium">CPF *</Label>
                      <Input required value={form.cpf} onChange={(e) => set("cpf", maskCPF(e.target.value))} placeholder="000.000.000-00" className="mt-1.5 bg-white border-[#E8E6E1] font-mono" data-testid="reg-cpf" />
                    </div>
                    <div>
                      <Label className="font-medium">Data de nascimento *</Label>
                      <Input type="date" required value={form.birthdate} onChange={(e) => set("birthdate", e.target.value)} className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-birthdate" />
                      <div className="text-[10px] text-[#9B9B9B] mt-1">Você precisa ter 18 anos ou mais.</div>
                    </div>
                  </div>
                  <div>
                    <Label className="font-medium">Nicho principal</Label>
                    <Select value={form.niche} onValueChange={(v) => set("niche", v)}>
                      <SelectTrigger className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-niche"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent className="bg-white">{NICHES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label className="font-medium">Cidade</Label>
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="São Paulo" className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-city" />
                </div>
                <div>
                  <Label className="font-medium">Estado</Label>
                  <Select value={form.state} onValueChange={(v) => set("state", v)}>
                    <SelectTrigger className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-state"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-medium">Como conheceu a Alta?</Label>
                <Select value={form.discovery_source} onValueChange={(v) => set("discovery_source", v)}>
                  <SelectTrigger className="mt-1.5 bg-white border-[#E8E6E1]" data-testid="reg-discovery"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="indicacao">Indicação de alguém</SelectItem>
                    <SelectItem value="google">Pesquisa no Google</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </section>

            {/* Section 3 — Consents */}
            <section className="space-y-3 bg-[#FAFAF7] border border-[#E8E6E1] rounded-lg p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] font-bold flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" /> 3. Termos e privacidade
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.accepted_terms} onCheckedChange={(v) => set("accepted_terms", !!v)} className="mt-0.5 border-[#9B9B9B] data-[state=checked]:bg-[#DC2626] data-[state=checked]:border-[#DC2626]" data-testid="reg-accept-terms" />
                <span className="text-sm text-[#0A0A0A] leading-relaxed">
                  Li e aceito os <Link to="/termos" target="_blank" className="text-[#DC2626] underline font-semibold">Termos de Uso</Link> da Alta Creators. *
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.accepted_privacy} onCheckedChange={(v) => set("accepted_privacy", !!v)} className="mt-0.5 border-[#9B9B9B] data-[state=checked]:bg-[#DC2626] data-[state=checked]:border-[#DC2626]" data-testid="reg-accept-privacy" />
                <span className="text-sm text-[#0A0A0A] leading-relaxed">
                  Li e aceito a <Link to="/privacidade" target="_blank" className="text-[#DC2626] underline font-semibold">Política de Privacidade</Link> (LGPD). *
                </span>
              </label>
              {isBrand && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.legal_responsible_accept} onCheckedChange={(v) => set("legal_responsible_accept", !!v)} className="mt-0.5 border-[#9B9B9B] data-[state=checked]:bg-[#DC2626] data-[state=checked]:border-[#DC2626]" data-testid="reg-legal" />
                  <span className="text-sm text-[#0A0A0A] leading-relaxed">
                    Confirmo que sou o responsável legal pela empresa informada e tenho autorização para contratar serviços em seu nome. *
                  </span>
                </label>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.marketing_opt_in} onCheckedChange={(v) => set("marketing_opt_in", !!v)} className="mt-0.5 border-[#9B9B9B] data-[state=checked]:bg-[#DC2626] data-[state=checked]:border-[#DC2626]" data-testid="reg-marketing" />
                <span className="text-sm text-[#4A4A4A] leading-relaxed">
                  Quero receber novidades, oportunidades e dicas por email. (opcional)
                </span>
              </label>
              <div className="text-[10px] text-[#9B9B9B] pt-1">* Campos obrigatórios</div>
            </section>

            {err && (
              <div className="text-sm text-[#DC2626] bg-[#FFF1F1] border border-[#FEE2E2] rounded-lg px-3 py-2" data-testid="reg-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !canSubmit}
              className="w-full bg-[#DC2626] hover:bg-[#B91C1C] glow-red text-white rounded-full py-6 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reg-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4" /> Criar conta de {isBrand ? "marca" : "creator"}
                </span>
              )}
            </Button>

            <div className="text-center text-sm text-[#6B6B6B]">
              Já tem conta? <Link to="/login" className="text-[#DC2626] hover:text-[#B91C1C] font-semibold" data-testid="reg-go-login">Entrar</Link>
            </div>
            <div className="text-center text-xs text-[#9B9B9B] pt-1">
              {isBrand ? (
                <>Quer se cadastrar como creator? <Link to="/cadastro/criador" className="text-[#DC2626] font-semibold">Clique aqui</Link></>
              ) : (
                <>Quer se cadastrar como marca? <Link to="/cadastro/marca" className="text-[#DC2626] font-semibold">Clique aqui</Link></>
              )}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
